/* ═══════════════════════════════════════════════════════════════════════════
   Start.jsx — The one-step evergreen door for Camp FaiBoWriMo
   Route: /#/start  (this is the ad destination)

   ── GRETA INTEGRATION NOTES ──────────────────────────────────────────────
   1. ROUTING: Register this component at path "/start" alongside the
      existing "/register" route. Do NOT modify Register.jsx — the live
      July flow stays untouched.

   2. SETTINGS KEY: This component reads `evergreen_registration_open`
      from settings_fbwm (separate from `registration_open`, which governs
      live camp). Add that key via the admin Evergreen module. If the key
      is absent, evergreen registration defaults to OPEN.

   3. DATA MODEL: Requires the new `trail_start_date` column (text,
      YYYY-MM-DD) on users_fbwm, and the new pass_type values
      'evergreen' / 'evergreen-aap'. Existing July rows are untouched.

   4. AUTH HANDOFF (the one Hub-side change worth making): after the
      webhook, this component checks the PHP response for an auth token.
      • If camp-fbwm-register.php is updated to return
        { "token": "<jwt>" } for source 'evergreen_start', the camper is
        redirected to /?token=... and AuthContext logs them straight in —
        a true one-step door with ZERO extra screens.
      • Until then, the fallback redirects to the Hub login page and they
        sign in with the password they just created. Works today; upgrade
        the PHP when ready. Do not gate entry behind email verification —
        verification moves to the Day 1 email (see Product Plan).

   5. CHECKOUT: EVERGREEN_AAP_CHECKOUT_URL below is a placeholder. Create
      the evergreen AAP product in Skillplate ($9 registration-moment
      price) and paste its checkout URL. The purchase webhook must flip
      pass_type to 'evergreen-aap' (mirror the existing allaccess flow).

   6. ENCHARGE: the Hub PHP should tag evergreen registrants (e.g.
      'evergreen-camp') so the delay-based Cabin Counselor drip starts.
      Payload already includes source: 'evergreen_start' to key off.
   ─────────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

// TODO (Jeremy): replace with the evergreen AAP checkout URL from Skillplate
const EVERGREEN_AAP_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/EVERGREEN_AAP_PRODUCT_ID';

const HUB_WEBHOOK_URL = 'https://gospelwiseauthor.app/camp-fbwm-register.php';
const HUB_LOGIN_URL = 'https://www.gospelwiseauthor.app/#/login?redirect=https://camp.faibowrimo.com';

/**
 * trail_start_date = the date Day 1 begins — NOT the signup date.
 * Computed as TOMORROW in the camper's local timezone, stored as a plain
 * YYYY-MM-DD string. Day 0 (orientation) needs no field: any local date
 * before trail_start_date renders as Day 0, open the instant they land.
 * Because gating compares local dates client-side, every camper's days
 * flip at their own midnight — no timezone field required.
 */
function computeTrailStartDate() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function Start() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'existing'
  const [step, setStep] = useState(1); // 1 = the door, 2 = you're in (+ AAP moment)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Evergreen registration toggle (the pause-for-live-rounds switch)
  const [evergreenOpen, setEvergreenOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);

  // If the Hub PHP returns a token, we can log the camper straight in
  const [authToken, setAuthToken] = useState(null);

  // Registered email carried into step 2 (works for both tabs)
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [existingEmail, setExistingEmail] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_fbwm');
        const data = Array.isArray(res) ? res : res.data || [];
        const setting = data.find(
          (s) => s.key === 'evergreen_registration_open' || s.setting_key === 'evergreen_registration_open'
        );
        // Absent key = open by default; only an explicit 'false' closes the door
        if (setting && setting.value === 'false') {
          setEvergreenOpen(false);
        }
      } catch (e) {
        console.error('Failed to check evergreen registration status', e);
      } finally {
        setCheckingReg(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Opening the camp gate...</p>
        </div>
      </div>
    );
  }

  /* ── Evergreen paused (a live camp round is in session) ── */
  if (!evergreenOpen) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber">
            <SafeIcon name="Tent" className="text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-2">A Live Camp Is In Session</h1>
          <p className="text-lg text-muted mb-6 leading-relaxed">
            Self-paced registration is paused while a live round of Camp FaiBoWriMo is underway —
            and the live experience is even better. Join us there!
          </p>
          <a href="https://join.crossandpen.com/p/camp-faibowrimo/" className="block w-full sm:w-auto mt-6">
            <Button className="py-4 px-10 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold shadow-md">
              See the Live Camp
            </Button>
          </a>
          <div className="pt-8 mt-8 border-t border-tan text-sm">
            <span className="text-muted">Already have a Camp account? </span>
            <a href={HUB_LOGIN_URL} className="text-forest-green font-bold hover:underline">
              Log in here
            </a>
          </div>
        </Card>
      </div>
    );
  }

  /* ── Shared: fire the Hub webhook, capture a token if the PHP returns one ── */
  const fireHubWebhook = async (payload) => {
    try {
      const res = await fetch(HUB_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_camp_secure_sync_2026_xyz',
          app_slug: 'camp',
          ...payload,
        }),
      });
      // If the Hub PHP has been upgraded to return a token for evergreen
      // registrations, capture it for instant login. Fails soft otherwise.
      try {
        const data = await res.json();
        if (data && data.token) setAuthToken(data.token);
      } catch (_) {
        /* non-JSON response is fine — fallback path handles it */
      }
    } catch (webhookErr) {
      console.warn('Hub webhook failed, but camp registration succeeded', webhookErr);
    }
  };

  /* ── New camper: the one-step door ── */
  const handleNewUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill out all fields to step into camp.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();

      // 0. Duplicate check — NCB's email filter is unreliable (case
      //    sensitivity), so fetch-all + client-side match is the pattern.
      const existingRes = await api.get('users_fbwm');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      const existing = rows.find(
        (u) => (u.email || '').trim().toLowerCase() === cleanEmail
      );
      if (existing) {
        setError('That email already has a Camp account — use the "I Have a GWA Account" tab to step back in.');
        setLoading(false);
        return;
      }

      const trailStart = computeTrailStartDate();

      // 1. Create camper profile directly in users_fbwm
      //    NCB native auth is NOT enabled — never call /user-auth/ endpoints.
      //    NCB auto-assigns the row ID; owner_id is backfilled after.
      //    NOTE: genre / gender / journey_stage are intentionally ABSENT —
      //    they moved to the Day 0 "set up your camp profile" exercise.
      const profileData = {
        name: form.name,
        email: cleanEmail,
        role: 'camper',
        pass_type: 'evergreen',
        trail_start_date: trailStart,
        first_login_complete: true,
        owner_id: 'pending', // satisfies NOT NULL; backfilled immediately after
      };

      const createRes = await api.post('users_fbwm', profileData);

      const userId =
        createRes?.data?.id ||
        createRes?.id ||
        (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);

      // 2. Backfill owner_id with the real NCB row ID
      if (userId) {
        await api.put('users_fbwm', userId, { owner_id: userId });
      }

      // 3. Hub webhook — creates the Hub account, tags Encharge for the
      //    Cabin Counselor drip. Do NOT gate entry on email verification;
      //    verification rides the Day 1 email instead.
      await fireHubWebhook({
        email: cleanEmail,
        name: form.name,
        password: form.password,
        trail_start_date: trailStart,
        source: 'evergreen_start',
      });

      // 4. You're in — Day 0 is open. Show the arrival screen (+ AAP moment).
      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  /* ── Existing GWA Hub user stepping onto the trail ── */
  const handleExistingUser = async (e) => {
    e.preventDefault();
    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    const cleanEmail = existingEmail.trim().toLowerCase();

    try {
      // Find their camper row (fetch-all + client-side match — NCB pattern)
      const existingRes = await api.get('users_fbwm');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      const existing = rows.find(
        (u) => (u.email || '').trim().toLowerCase() === cleanEmail
      );

      const trailStart = computeTrailStartDate();

      if (!existing) {
        // Hub user, never a camper: create their evergreen row
        const createRes = await api.post('users_fbwm', {
          name: '', // Hub sync / Day 0 profile fills this in
          email: cleanEmail,
          role: 'camper',
          pass_type: 'evergreen',
          trail_start_date: trailStart,
          first_login_complete: true,
          owner_id: 'pending',
        });
        const newId =
          createRes?.data?.id ||
          createRes?.id ||
          (Array.isArray(createRes?.data) ? createRes.data[0]?.id : null);
        if (newId) await api.put('users_fbwm', newId, { owner_id: newId });
      } else if (existing.pass_type === 'free') {
        // July free camper returning: their July window has closed —
        // give them a fresh evergreen trail.
        await api.put('users_fbwm', existing.id, {
          pass_type: 'evergreen',
          trail_start_date: trailStart,
        });
      } else if (
        existing.pass_type === 'evergreen' ||
        existing.pass_type === 'evergreen-aap'
      ) {
        // Already on (or finished) an evergreen trail: do NOT reset
        // trail_start_date here. Restarts are an admin decision — one
        // field edit in the Campers tab. Just let them back in.
      }
      // pass_type === 'allaccess': July AAP holder with ongoing access —
      // leave their row exactly as-is. Never downgrade an AAP.

      await fireHubWebhook({
        email: cleanEmail,
        action: 'grant_access',
        trail_start_date: trailStart,
        source: 'evergreen_start_existing',
      });

      setRegisteredEmail(cleanEmail);
      setStep(2);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  /* ── Step 2 continue: token → instant login; otherwise Hub login ── */
  const enterCamp = () => {
    if (authToken) {
      // Hub PHP returned a token — AuthContext will process it on arrival
      window.location.href = `https://camp.faibowrimo.com/?token=${encodeURIComponent(authToken)}`;
    } else {
      window.location.href = HUB_LOGIN_URL;
    }
  };

  const goToAapCheckout = () => {
    window.location.href = `${EVERGREEN_AAP_CHECKOUT_URL}?email=${encodeURIComponent(registeredEmail)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white">
          <SafeIcon name="Compass" className="text-3xl" />
        </div>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold text-dark-green">
              Your Week at Camp Starts Now
            </h1>
            <p className="text-muted">
              Register in under a minute — Day 0 opens the moment you're in.
            </p>

            <div className="flex border-b border-tan mb-6">
              <button
                className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
                onClick={() => { setActiveTab('new'); setError(''); }}
              >
                New Camper
              </button>
              <button
                className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted hover:text-dark-green bg-gray-50/50'}`}
                onClick={() => { setActiveTab('existing'); setError(''); }}
              >
                I Have a GWA Account
              </button>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md text-center">
                {error}
              </div>
            )}

            {activeTab === 'new' ? (
              <form onSubmit={handleNewUser} className="space-y-4 text-left">
                <Input
                  label="Full Name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. Jane Doe"
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="you@email.com"
                />
                <Input
                  label="Create a Password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
                <div className="pt-4">
                  <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                    {loading ? 'Opening the gate...' : 'Step Into Camp — Free'}
                    {!loading && <SafeIcon name="ArrowRight" className="ml-2" />}
                  </Button>
                </div>
                <p className="text-xs text-muted text-center pt-2">
                  Free for one full week. No card required.
                </p>
              </form>
            ) : (
              <form onSubmit={handleExistingUser} className="space-y-6 text-left">
                <div className="bg-amber/10 p-4 rounded-md border border-amber/30">
                  <p className="text-sm text-dark-green leading-relaxed">
                    Already part of the GospelWise Author family? Enter your email and
                    we'll set your trail — no new account needed.
                  </p>
                </div>
                <Input
                  label="Your GWA Email Address"
                  type="email"
                  value={existingEmail}
                  onChange={(e) => setExistingEmail(e.target.value)}
                  required
                  placeholder="you@email.com"
                />
                <div className="pt-4">
                  <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                    {loading ? 'Setting your trail...' : 'Step Into Camp — Free'}
                  </Button>
                </div>
              </form>
            )}

            <div className="pt-6 mt-4 border-t border-tan text-sm text-center">
              <span className="text-muted">Already registered for this camp? </span>
              <a href={HUB_LOGIN_URL} className="text-forest-green font-bold hover:underline">
                Log in here
              </a>
            </div>
          </>
        )}

        {/* ── Step 2: You're in — Day 0 open + the $9 AAP moment ── */}
        {step === 2 && (
          <div className="space-y-5 py-2">
            <div className="mx-auto w-16 h-16 bg-forest-green/10 rounded-full flex items-center justify-center">
              <SafeIcon name="Tent" className="text-3xl text-forest-green" />
            </div>
            <h2 className="text-2xl font-bold text-dark-green">Welcome to Camp — You're In!</h2>
            <p className="text-muted leading-relaxed">
              Day 0 is open right now: get oriented, set up your camp profile, and meet
              the four personas. Your Day 1 Morning Assembly arrives tomorrow morning.
            </p>

            <div className="bg-amber/10 border border-amber/30 rounded-md p-5 text-left space-y-3">
              <p className="font-bold text-dark-green">
                One-time offer — the All-Access Pass, just $9 today
              </p>
              <p className="text-sm text-dark-green leading-relaxed">
                Keep everything you build at camp forever — plus personal daily emails
                from Jeremy, the Mess Hall Implementation Library, the private podcast,
                the Self-Letter Studio, your permanent Compass archive, the printable
                Camp Kit, and the GWA Roadmap Course &amp; Publishing Handbook.
              </p>
              <p className="text-xs text-muted">
                $9 right now at registration · $19 during your camp week · closed after
                your week ends.
              </p>
              <Button
                className="w-full py-3 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold"
                onClick={goToAapCheckout}
              >
                Add All-Access — $9
              </Button>
            </div>

            <Button variant="outline" className="w-full py-3 border-2 border-forest-green" onClick={enterCamp}>
              Continue to Camp — Free <SafeIcon name="ArrowRight" className="ml-2" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}